import { useState, useEffect } from "react";
import { useDebounce } from "./hooks/useDebounce";
import LoginForm from "./Components/LoginForm";
import WomenTab from "./Components/WomenTab";
import NewsTab from "./Components/NewsTab";
import MessagesTab from "./Components/MessagesTab";
import CategoriesTab from "./Components/CategoriesTab";
import PlacesTab from "./Components/PlacesTab";
import WomanModal from "./Components/WomanModal";
import NewsModal from "./Components/NewsModal";
import CategoryModal from "./Components/CategoryModal";
import PlaceModal from "./Components/PlaceModal";
import API from "../../config/api";
import { getNewsLargeImageUrl, getWomanLargeImageUrl } from "../../utils/imageUrl";
import "./Admin.sass";

const API_URL = API.admin;

function Admin() {
    // Auth state
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");

    // Tab state
    const [activeTab, setActiveTab] = useState("women");

    // Data state
    const [women, setWomen] = useState([]);
    const [news, setNews] = useState([]);
    const [messages, setMessages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [places, setPlaces] = useState([]);

    // Women modal state
    const [editingWoman, setEditingWoman] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [womanTranslating, setWomanTranslating] = useState(false);

    // Women filters & pagination
    const [filters, setFilters] = useState({ name: "", category_id: "", place_id: "" });
    const [sortConfig, setSortConfig] = useState({ field: "id", direction: "asc" });
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(false);
    const [translationStatus, setTranslationStatus] = useState(null);
    const [translating, setTranslating] = useState(false);
    const [monthlyCandidateUpdatingIds, setMonthlyCandidateUpdatingIds] = useState([]);

    // News modal state
    const [editingNews, setEditingNews] = useState(null);
    const [showNewsModal, setShowNewsModal] = useState(false);
    const [newsImageFile, setNewsImageFile] = useState(null);
    const [newsImagePreview, setNewsImagePreview] = useState(null);
    const [newsUploading, setNewsUploading] = useState(false);
    const [newsTranslating, setNewsTranslating] = useState(false);

    // Category modal state
    const [editingCategory, setEditingCategory] = useState(null);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [categoryTranslating, setCategoryTranslating] = useState(false);

    // Place modal state
    const [editingPlace, setEditingPlace] = useState(null);
    const [showPlaceModal, setShowPlaceModal] = useState(false);
    const [placeTranslating, setPlaceTranslating] = useState(false);

    const debouncedNameFilter = useDebounce(filters.name, 300);

    // ==================== EFFECTS ====================
    useEffect(() => {
        fetch(`${API_URL}?action=checkAuth`, { credentials: "include" })
            .then((res) => res.json())
            .then((data) => setIsLoggedIn(data.loggedIn))
            .catch(console.error);
    }, []);

    useEffect(() => {
        if (isLoggedIn) {
            loadNews();
            loadMessages();
            loadCategories();
            loadPlaces();
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (isLoggedIn) {
            loadWomen();
        }
    }, [isLoggedIn, debouncedNameFilter, filters.category_id, filters.place_id, sortConfig, pagination.page]);

    useEffect(() => {
        setPagination((p) => ({ ...p, page: 1 }));
    }, [debouncedNameFilter]);

    // ==================== AUTH ====================
    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_URL}?action=login`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password }),
            });
            const data = await res.json();
            if (data.success) {
                setIsLoggedIn(true);
                setLoginError("");
            } else {
                setLoginError(data.error);
            }
        } catch (err) {
            setLoginError("Eroare de conexiune");
        }
    };

    const handleLogout = async () => {
        await fetch(`${API_URL}?action=logout`, { credentials: "include" });
        setIsLoggedIn(false);
    };

    // ==================== TRANSLATION ====================
    // Asks the backend to translate Romanian fields; returns { field_ru, field_en } for each one sent.
    const requestTranslations = async (fields) => {
        const res = await fetch(`${API_URL}?action=translateFields`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ fields }),
        });
        const data = await res.json();

        if (!data.success) {
            throw new Error(data.error || "Eroare la traducere");
        }

        return data.data || {};
    };

    // ==================== LOAD DATA ====================
    const loadWomen = async () => {
        setLoading(true);
        const params = new URLSearchParams({
            action: "getWomen",
            page: pagination.page,
            limit: pagination.limit,
            sortField:
                sortConfig.field === "category"
                    ? "category_name"
                    : sortConfig.field === "place"
                      ? "place_name"
                      : sortConfig.field,
            sortDir: sortConfig.direction,
        });
        if (debouncedNameFilter) params.append("name", debouncedNameFilter);
        if (filters.category_id) params.append("category_id", filters.category_id);
        if (filters.place_id) params.append("place_id", filters.place_id);

        const res = await fetch(`${API_URL}?${params}`, { credentials: "include" });
        const data = await res.json();
        if (data.success) {
            setWomen(data.data);
            setPagination((prev) => ({ ...prev, ...data.pagination }));
        }
        setLoading(false);
    };

    const loadMessages = async () => {
        const res = await fetch(`${API_URL}?action=getMessages`, { credentials: "include" });
        const data = await res.json();
        if (data.success) setMessages(data.data);
    };

    const loadCategories = async () => {
        const res = await fetch(`${API_URL}?action=getCategories`, { credentials: "include" });
        const data = await res.json();
        if (data.success) setCategories(data.data);
    };

    const loadPlaces = async () => {
        const res = await fetch(`${API_URL}?action=getPlaces`, { credentials: "include" });
        const data = await res.json();
        if (data.success) setPlaces(data.data);
    };

    // ==================== WOMEN HANDLERS ====================
    const handleSort = (field) => {
        const newDirection = sortConfig.field === field && sortConfig.direction === "asc" ? "desc" : "asc";
        setSortConfig({ field, direction: newDirection });
        setPagination((p) => ({ ...p, page: 1 }));
    };

    const handleFilterChange = (key, value) => {
        setFilters((f) => ({ ...f, [key]: value }));
        if (key !== "name") {
            setPagination((p) => ({ ...p, page: 1 }));
        }
    };

    const openEditModal = (woman = null) => {
        setEditingWoman(
            woman || {
                name: "",
                surname: "",
                achievement: "",
                achievement_ru: "",
                achievement_en: "",
                description: "",
                description_ru: "",
                description_en: "",
                sources: "",
                image: "",
                category_id: "",
                place_id: "",
                is_monthly_candidate: 0,
            },
        );
        setImageFile(null);
        setImagePreview(woman?.image ? getWomanLargeImageUrl(woman.image) : null);
        setShowModal(true);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const uploadImage = async () => {
        if (!imageFile) return editingWoman.image;

        const formData = new FormData();
        formData.append("image", imageFile);

        const res = await fetch(`${API_URL}?action=uploadImage`, {
            method: "POST",
            credentials: "include",
            body: formData,
        });
        const data = await res.json();
        if (data.success) {
            return data.filename;
        } else {
            throw new Error(data.error);
        }
    };

    const persistWoman = async (woman) => {
        const imageName = await uploadImage();
        const womanData = { ...woman, image: imageName };
        const isNew = !woman.id;
        const url = isNew ? `${API_URL}?action=createWoman` : `${API_URL}?action=updateWoman&id=${woman.id}`;
        const method = isNew ? "POST" : "PUT";

        const res = await fetch(url, {
            method,
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(womanData),
        });
        const data = await res.json();

        if (!data.success) {
            throw new Error(data.error || "Eroare la salvare");
        }

        setShowModal(false);
        loadWomen();
    };

    // Plain save: stores the three language tabs exactly as they are, no automatic translation.
    const saveWoman = async (e) => {
        e.preventDefault();
        setUploading(true);

        try {
            await persistWoman(editingWoman);
        } catch (err) {
            alert(err.message);
        } finally {
            setUploading(false);
        }
    };

    const translateWomanFields = async (woman) => {
        const translated = await requestTranslations({
            achievement: woman.achievement || "",
            description: woman.description || "",
        });

        return { ...woman, ...translated };
    };

    // Fills the RUS/ENG tabs from the Romanian text so they can be reviewed before saving.
    const translateWomanWithAi = async () => {
        setWomanTranslating(true);

        try {
            const translatedWoman = await translateWomanFields(editingWoman);
            setEditingWoman((current) => ({
                ...current,
                achievement_ru: translatedWoman.achievement_ru ?? current.achievement_ru,
                achievement_en: translatedWoman.achievement_en ?? current.achievement_en,
                description_ru: translatedWoman.description_ru ?? current.description_ru,
                description_en: translatedWoman.description_en ?? current.description_en,
            }));
        } catch (err) {
            alert(err.message);
        } finally {
            setWomanTranslating(false);
        }
    };

    const saveWomanAndTranslate = async () => {
        if (!editingWoman?.name?.trim() || !editingWoman?.surname?.trim()) {
            alert("Numele și prenumele sunt obligatorii.");
            return;
        }

        const hasTranslations = ["achievement_ru", "achievement_en", "description_ru", "description_en"].some((field) =>
            String(editingWoman[field] || "").trim(),
        );

        if (
            hasTranslations &&
            !window.confirm("Textele din filele RUS și ENG vor fi înlocuite cu traducerea automată. Continui?")
        ) {
            return;
        }

        setWomanTranslating(true);

        try {
            const translatedWoman = await translateWomanFields(editingWoman);
            setEditingWoman(translatedWoman);
            setUploading(true);
            await persistWoman(translatedWoman);
        } catch (err) {
            alert(err.message);
        } finally {
            setWomanTranslating(false);
            setUploading(false);
        }
    };

    const deleteWoman = async (id) => {
        if (!window.confirm("Sigur doriți să ștergeți?")) return;
        const res = await fetch(`${API_URL}?action=deleteWoman&id=${id}`, {
            method: "DELETE",
            credentials: "include",
        });
        const data = await res.json();
        if (data.success) {
            loadWomen();
        }
    };

    const loadNews = async () => {
        const res = await fetch(`${API_URL}?action=getNewsAdmin`, { credentials: "include" });
        const data = await res.json();
        if (data.success) setNews(data.data);
    };

    const toggleMonthlyCandidate = async (id, checked) => {
        const nextValue = checked ? 1 : 0;
        const previousValue = checked ? 0 : 1;

        setWomen((currentWomen) =>
            currentWomen.map((woman) =>
                String(woman.id) === String(id) ? { ...woman, is_monthly_candidate: nextValue } : woman,
            ),
        );
        setMonthlyCandidateUpdatingIds((ids) => [...ids, id]);

        try {
            const res = await fetch(`${API_URL}?action=toggleMonthlyCandidate&id=${id}`, {
                method: "PUT",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ is_monthly_candidate: nextValue }),
            });
            const responseText = await res.text();
            let data;

            try {
                data = JSON.parse(responseText);
            } catch {
                throw new Error("Backend-ul a răspuns invalid. Verifică eroarea PHP din backend/admin.php.");
            }

            if (!data.success) {
                throw new Error(data.error || "Eroare la actualizare");
            }
        } catch (err) {
            setWomen((currentWomen) =>
                currentWomen.map((woman) =>
                    String(woman.id) === String(id) ? { ...woman, is_monthly_candidate: previousValue } : woman,
                ),
            );
            alert(err.message);
        } finally {
            setMonthlyCandidateUpdatingIds((ids) => ids.filter((updatingId) => String(updatingId) !== String(id)));
        }
    };

    // ==================== NEWS HANDLERS ====================
    const openNewsModal = (item = null) => {
        setEditingNews(
            item || {
                title: "",
                title_ru: "",
                title_en: "",
                content: "",
                content_ru: "",
                content_en: "",
                image_url: "",
                source_url: "",
                is_published: 1,
                sort_order: 0,
                published_at: new Date().toISOString().slice(0, 19).replace("T", " "),
            },
        );
        setNewsImageFile(null);
        setNewsImagePreview(item?.image_url ? getNewsLargeImageUrl(item.image_url) : null);
        setShowNewsModal(true);
    };

    const handleNewsImageChange = (e) => {
        const file = e.target.files[0];

        if (file) {
            setNewsImageFile(file);
            setNewsImagePreview(URL.createObjectURL(file));
        }
    };

    const uploadNewsImage = async () => {
        if (!newsImageFile) return editingNews.image_url || "";

        const formData = new FormData();
        formData.append("image", newsImageFile);

        const res = await fetch(`${API_URL}?action=uploadNewsImage`, {
            method: "POST",
            credentials: "include",
            body: formData,
        });
        const data = await res.json();

        if (data.success) {
            return data.filename;
        }

        throw new Error(data.error || "Eroare la incarcarea imaginii");
    };

    const translateNewsWithAi = async () => {
        if (!editingNews?.title?.trim() || !editingNews?.content?.trim()) {
            alert("Completeaza titlul si continutul in romana inainte de traducere.");
            return;
        }

        setNewsTranslating(true);

        try {
            const res = await fetch(`${API_URL}?action=translateNews`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    title: editingNews.title,
                    content: editingNews.content,
                }),
            });
            const data = await res.json();

            if (!data.success) {
                throw new Error(data.error || "Eroare la traducere");
            }

            setEditingNews((current) => ({
                ...current,
                title_ru: data.data?.title_ru || current.title_ru || "",
                title_en: data.data?.title_en || current.title_en || "",
                content_ru: data.data?.content_ru || current.content_ru || "",
                content_en: data.data?.content_en || current.content_en || "",
            }));
        } catch (err) {
            alert(err.message);
        } finally {
            setNewsTranslating(false);
        }
    };

    const persistNews = async (item) => {
        const imageUrl = await uploadNewsImage();
        const newsPayload = {
            ...item,
            image_url: imageUrl,
            excerpt: "",
            excerpt_ru: "",
            excerpt_en: "",
        };

        const isNew = !item.id;
        const url = isNew ? `${API_URL}?action=createNews` : `${API_URL}?action=updateNews&id=${item.id}`;
        const method = isNew ? "POST" : "PUT";

        const res = await fetch(url, {
            method,
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newsPayload),
        });
        const data = await res.json();

        if (!data.success) {
            throw new Error(data.error || "Eroare la salvare");
        }

        setShowNewsModal(false);
        loadNews();
    };

    // Plain save: stores the three language tabs exactly as they are, no automatic translation.
    const saveNews = async (e) => {
        e.preventDefault();

        if (!editingNews?.title?.trim() || !editingNews?.content?.trim()) {
            alert("Titlul si continutul in romana sunt obligatorii.");
            return;
        }

        setNewsUploading(true);

        try {
            await persistNews(editingNews);
        } catch (err) {
            alert(err.message);
        } finally {
            setNewsUploading(false);
        }
    };

    const saveNewsAndTranslate = async () => {
        if (!editingNews?.title?.trim() || !editingNews?.content?.trim()) {
            alert("Titlul si continutul in romana sunt obligatorii.");
            return;
        }

        const hasTranslations = ["title_ru", "title_en", "content_ru", "content_en"].some((field) =>
            String(editingNews[field] || "").trim(),
        );

        if (
            hasTranslations &&
            !window.confirm("Textele din filele RUS și ENG vor fi înlocuite cu traducerea automată. Continui?")
        ) {
            return;
        }

        setNewsTranslating(true);

        try {
            const translated = await requestTranslations({ title: editingNews.title, content: editingNews.content });
            const translatedNews = { ...editingNews, ...translated };
            setEditingNews(translatedNews);
            setNewsUploading(true);
            await persistNews(translatedNews);
        } catch (err) {
            alert(err.message);
        } finally {
            setNewsTranslating(false);
            setNewsUploading(false);
        }
    };

    const deleteNews = async (id) => {
        if (!window.confirm("Sigur doriti sa stergeti aceasta noutate?")) return;
        const res = await fetch(`${API_URL}?action=deleteNews&id=${id}`, {
            method: "DELETE",
            credentials: "include",
        });
        const data = await res.json();
        if (data.success) {
            loadNews();
        } else {
            alert(data.error || "Eroare la stergere");
        }
    };

    // ==================== MESSAGE HANDLERS ====================
    const deleteMessage = async (id) => {
        if (!window.confirm("Sigur doriți să ștergeți?")) return;
        const res = await fetch(`${API_URL}?action=deleteMessage&id=${id}`, {
            method: "DELETE",
            credentials: "include",
        });
        const data = await res.json();
        if (data.success) loadMessages();
    };

    const markAsRead = async (id) => {
        await fetch(`${API_URL}?action=markRead&id=${id}`, {
            method: "PUT",
            credentials: "include",
        });
        loadMessages();
    };

    // ==================== CATEGORY HANDLERS ====================
    const openCategoryModal = (category = null) => {
        setEditingCategory(
            category
                ? { ...category, name_ru: category.name_ru || "", name_en: category.name_en || "" }
                : { name: "", name_ru: "", name_en: "", color: "#b33771" },
        );
        setShowCategoryModal(true);
    };

    const saveCategory = async (e) => {
        e.preventDefault();
        const isNew = !editingCategory.id;
        const url = isNew
            ? `${API_URL}?action=createCategory`
            : `${API_URL}?action=updateCategory&id=${editingCategory.id}`;
        const method = isNew ? "POST" : "PUT";

        const res = await fetch(url, {
            method,
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(editingCategory),
        });
        const data = await res.json();
        if (data.success) {
            setShowCategoryModal(false);
            loadCategories();
        } else {
            alert(data.error);
        }
    };

    const translateCategoryWithAi = async () => {
        setCategoryTranslating(true);

        try {
            const translated = await requestTranslations({ name: editingCategory.name || "" });
            setEditingCategory((current) => ({ ...current, ...translated }));
        } catch (err) {
            alert(err.message);
        } finally {
            setCategoryTranslating(false);
        }
    };

    const deleteCategory = async (id) => {
        if (!window.confirm("Sigur doriți să ștergeți această categorie?")) return;
        const res = await fetch(`${API_URL}?action=deleteCategory&id=${id}`, {
            method: "DELETE",
            credentials: "include",
        });
        const data = await res.json();
        if (data.success) loadCategories();
    };

    // ==================== PLACE HANDLERS ====================
    const openPlaceModal = (place = null) => {
        setEditingPlace(
            place
                ? { ...place, name_ru: place.name_ru || "", name_en: place.name_en || "" }
                : { name: "", name_ru: "", name_en: "" },
        );
        setShowPlaceModal(true);
    };

    const savePlace = async (e) => {
        e.preventDefault();
        const isNew = !editingPlace.id;
        const url = isNew ? `${API_URL}?action=createPlace` : `${API_URL}?action=updatePlace&id=${editingPlace.id}`;
        const method = isNew ? "POST" : "PUT";

        const res = await fetch(url, {
            method,
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(editingPlace),
        });
        const data = await res.json();
        if (data.success) {
            setShowPlaceModal(false);
            loadPlaces();
        } else {
            alert(data.error);
        }
    };

    const translatePlaceWithAi = async () => {
        setPlaceTranslating(true);

        try {
            const translated = await requestTranslations({ name: editingPlace.name || "" });
            setEditingPlace((current) => ({ ...current, ...translated }));
        } catch (err) {
            alert(err.message);
        } finally {
            setPlaceTranslating(false);
        }
    };

    const deletePlace = async (id) => {
        if (!window.confirm("Sigur doriți să ștergeți acest loc?")) return;
        const res = await fetch(`${API_URL}?action=deletePlace&id=${id}`, {
            method: "DELETE",
            credentials: "include",
        });
        const data = await res.json();
        if (data.success) loadPlaces();
    };

    // ==================== RENDER ====================
    if (!isLoggedIn) {
        return (
            <LoginForm password={password} setPassword={setPassword} onLogin={handleLogin} loginError={loginError} />
        );
    }

    const handleExportExcel = () => {
        window.open(`${API_URL}?action=exportExcel`, "_blank");
    };

    // Read-only reports (dry run): nothing is changed until ?apply=1 is added by hand.
    const handleOpenCleanupReport = () => {
        window.open(`${API.contentAudit}?limit=200`, "_blank");
    };

    const handleOpenPrivacyAudit = () => {
        window.open(`${API.contentAudit}?mode=audit&limit=500`, "_blank");
    };

    const handleTranslateMissing = async () => {
        if (
            !window.confirm(
                "Traducerea va completa automat câmpurile lipsă în rusă și engleză pentru femei, categorii și locuri. Continui?",
            )
        ) {
            return;
        }

        setTranslating(true);
        setTranslationStatus("Se verifică traducerile lipsă...");

        try {
            let remaining = null;
            let totalProcessed = 0;

            do {
                const res = await fetch(`${API_URL}?action=translateMissing`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ limit: 5 }),
                });
                const data = await res.json();

                if (!data.success) {
                    throw new Error(data.error || "Eroare la traducere");
                }

                totalProcessed += data.processedCount || 0;
                remaining = data.remaining;
                setTranslationStatus(
                    `Traduse în acest run: ${totalProcessed}. Rămase: ${remaining?.total || 0} (femei: ${
                        remaining?.women || 0
                    }, categorii: ${remaining?.categories || 0}, locuri: ${remaining?.places || 0}).`,
                );

                if ((data.processedCount || 0) === 0) {
                    break;
                }
            } while ((remaining?.total || 0) > 0);

            setTranslationStatus(`Traduceri finalizate. Elemente procesate: ${totalProcessed}.`);
            loadWomen();
            loadCategories();
            loadPlaces();
        } catch (err) {
            setTranslationStatus(`Eroare traducere: ${err.message}`);
        } finally {
            setTranslating(false);
        }
    };

    return (
        <div className="admin general-wrapper">
            <div className="admin__header">
                <h1>Panou Admin</h1>
                <div className="admin__header-actions">
                    <button onClick={handleTranslateMissing} className="admin__translate-ai" disabled={translating}>
                        {translating ? "Se traduce..." : "Tradu lipsurile cu AI"}
                    </button>
                    <button
                        onClick={handleOpenCleanupReport}
                        className="admin__export-excel"
                        title="Arată ce fraze de umplutură ar fi eliminate din descrieri. Nu modifică nimic.">
                        Raport curățare texte
                    </button>
                    <button
                        onClick={handleOpenPrivacyAudit}
                        className="admin__export-excel"
                        title="Profiluri cu pasaje care pot conține date private (adrese, telefoane, sănătate, familie).">
                        Audit date private
                    </button>
                    <button onClick={handleExportExcel} className="admin__export-excel">
                        ⬇ Export Excel
                    </button>
                    <button onClick={handleLogout} className="admin__logout">
                        Deconectare
                    </button>
                </div>
            </div>
            {translationStatus && <div className="admin__translation-status">{translationStatus}</div>}

            <div className="admin__tabs">
                <button className={activeTab === "women" ? "active" : ""} onClick={() => setActiveTab("women")}>
                    Femei ({pagination.total})
                </button>
                <button className={activeTab === "news" ? "active" : ""} onClick={() => setActiveTab("news")}>
                    Noutati ({news.length})
                </button>
                <button
                    className={activeTab === "categories" ? "active" : ""}
                    onClick={() => setActiveTab("categories")}>
                    Categorii ({categories.length})
                </button>
                <button className={activeTab === "places" ? "active" : ""} onClick={() => setActiveTab("places")}>
                    Locuri ({places.length})
                </button>
                <button className={activeTab === "messages" ? "active" : ""} onClick={() => setActiveTab("messages")}>
                    Mesaje ({messages.filter((m) => parseInt(m.is_read) === 0).length} necitite)
                </button>
            </div>

            {activeTab === "women" && (
                <WomenTab
                    women={women}
                    categories={categories}
                    places={places}
                    filters={filters}
                    pagination={pagination}
                    loading={loading}
                    sortConfig={sortConfig}
                    onFilterChange={handleFilterChange}
                    onClearFilters={() => setFilters({ name: "", category_id: "", place_id: "" })}
                    onSort={handleSort}
                    onAdd={() => openEditModal()}
                    onEdit={openEditModal}
                    onDelete={deleteWoman}
                    onPageChange={(page) => setPagination((p) => ({ ...p, page }))}
                    onToggleMonthlyCandidate={toggleMonthlyCandidate}
                    monthlyCandidateUpdatingIds={monthlyCandidateUpdatingIds}
                />
            )}

            {activeTab === "news" && (
                <NewsTab news={news} onAdd={() => openNewsModal()} onEdit={openNewsModal} onDelete={deleteNews} />
            )}

            {activeTab === "messages" && (
                <MessagesTab messages={messages} onMarkRead={markAsRead} onDelete={deleteMessage} />
            )}

            {activeTab === "categories" && (
                <CategoriesTab
                    categories={categories}
                    onAdd={() => openCategoryModal()}
                    onEdit={openCategoryModal}
                    onDelete={deleteCategory}
                />
            )}

            {activeTab === "places" && (
                <PlacesTab
                    places={places}
                    onAdd={() => openPlaceModal()}
                    onEdit={openPlaceModal}
                    onDelete={deletePlace}
                />
            )}

            {showModal && editingWoman && (
                <WomanModal
                    woman={editingWoman}
                    setWoman={setEditingWoman}
                    categories={categories}
                    places={places}
                    imagePreview={imagePreview}
                    uploading={uploading}
                    translating={womanTranslating}
                    onImageChange={handleImageChange}
                    onTranslate={translateWomanWithAi}
                    onSave={saveWoman}
                    onSaveAndTranslate={saveWomanAndTranslate}
                    onClose={() => setShowModal(false)}
                />
            )}

            {showNewsModal && editingNews && (
                <NewsModal
                    news={editingNews}
                    setNews={setEditingNews}
                    imagePreview={newsImagePreview}
                    uploading={newsUploading}
                    translating={newsTranslating}
                    onImageChange={handleNewsImageChange}
                    onTranslate={translateNewsWithAi}
                    onSave={saveNews}
                    onSaveAndTranslate={saveNewsAndTranslate}
                    onClose={() => setShowNewsModal(false)}
                />
            )}

            {showCategoryModal && editingCategory && (
                <CategoryModal
                    category={editingCategory}
                    setCategory={setEditingCategory}
                    translating={categoryTranslating}
                    onTranslate={translateCategoryWithAi}
                    onSave={saveCategory}
                    onClose={() => setShowCategoryModal(false)}
                />
            )}

            {showPlaceModal && editingPlace && (
                <PlaceModal
                    place={editingPlace}
                    setPlace={setEditingPlace}
                    translating={placeTranslating}
                    onTranslate={translatePlaceWithAi}
                    onSave={savePlace}
                    onClose={() => setShowPlaceModal(false)}
                />
            )}
        </div>
    );
}

export default Admin;
